import type { PublicHoursByDealership } from '@/lib/dealership-hours';

/**
 * Sucursales y horarios del tenant tal como ya los tiene el store del cliente.
 *
 * La fila del tenant llega con `dealerships(*)` embebidas (misma consulta de
 * siempre) y, desde que el layout la resuelve en el servidor, también con
 * `dealership_hours` (la RPC de horarios, que el layout ya llamaba para el
 * JSON-LD). Las secciones de contacto ("Cómo llegar", el formulario de
 * contacto y su versión del builder) pedían las dos cosas de nuevo, cada una
 * por su cuenta: con esto las toman del store y solo salen a la base si el
 * store no las trae.
 *
 * Devuelven `null` cuando el dato no está —no un arreglo vacío—, para que el
 * caller distinga "no hay sucursales" de "no sé" y en el segundo caso consulte
 * como siempre.
 */

export function sucursalesDelCliente<T = Record<string, any>>(
  client: { dealerships?: unknown } | null | undefined
): T[] | null {
  const ds = client?.dealerships;
  if (!Array.isArray(ds)) return null;
  // Sin reordenar: estas secciones siempre tomaron "la principal" como la
  // primera fila que devolvía `select * … eq(client_id)` sin ORDER BY, y el
  // embed `dealerships(*)` devuelve las filas en ese mismo orden físico.
  // Ordenar por id acá cambiaba el teléfono del contacto en Tradecars
  // (sucursal 461 antes que la 47) respecto de lo que ve hoy el visitante.
  return [...(ds as T[])];
}

export function horasDelCliente(
  client: { dealership_hours?: unknown } | null | undefined
): PublicHoursByDealership | null {
  const h = client?.dealership_hours;
  if (!h || typeof h !== 'object' || Array.isArray(h)) return null;
  return h as PublicHoursByDealership;
}
